import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import {
  providersAPI, regionsAPI, vmTypesAPI, osTypesAPI, disksAPI,
  databaseTypesAPI, databaseConfigsAPI, databaseEditionsAPI,
  addonsAPI, addonOptionsAPI, pricingAPI, defaultAddonsAPI,
  environmentsAPI, powerTypesAPI, powerAddonsAPI, powerDefaultAddonsAPI,
  powerAddonOptionsAPI, backupPricingAPI, drTestingPricingAPI,
} from '../../services/api';
import {
  Provider, Region, Environment, VMType, OSType, Disk, DatabaseType,
  DatabaseConfig, DatabaseEdition, Addon, AddonOption,
  VMPricingRow, DiskPricingRow, AddonPricingRow, DatabasePricingRow,
  AddonOptionPricingRow, DatabaseEditionPricingRow,
  PowerType, PowerAddon, PowerAddonOption, PowerVMPricingRow, PowerAddonPricingRow, PowerAddonOptionPricingRow,
  PowerDiskPricingRow,
  BackupPricingRow, DatabaseBackupPricingRow, DatabaseExtraStoragePricingRow, DRTestingPricingRow,
} from '../../types';
import { NormalizedDefaultAddon, NormalizedPowerDefaultAddon } from './types';

export const useQuoteData = () => {
  const { quoteId: routeQuoteIdParam } = useParams();
  const location = useLocation();
  const queryQuoteId = new URLSearchParams(location.search).get('load');
  const quoteIdFromRouteOrQuery = routeQuoteIdParam ?? queryQuoteId ?? null;

  /* -------- Catalog / Config state -------- */
  const [providers, setProviders] = useState<Provider[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [vmTypes, setVMTypes] = useState<VMType[]>([]);
  const [osTypes, setOSTypes] = useState<OSType[]>([]);
  const [disks, setDisks] = useState<Disk[]>([]);
  const [databaseTypes, setDatabaseTypes] = useState<DatabaseType[]>([]);
  const [databaseConfigs, setDatabaseConfigs] = useState<DatabaseConfig[]>([]);
  const [databaseEditions, setDatabaseEditions] = useState<DatabaseEdition[]>([]);
  const [addons, setAddons] = useState<Addon[]>([]);
  const [addonOptions, setAddonOptions] = useState<Record<number, AddonOption[]>>({});
  const [powerTypes, setPowerTypes] = useState<PowerType[]>([]);
  const [powerAddons, setPowerAddons] = useState<PowerAddon[]>([]);
  const [powerAddonOptions, setPowerAddonOptions] = useState<Record<number, PowerAddonOption[]>>({});

  /* -------- Pricing maps -------- */
  const [vmPricing, setVMPricing] = useState<Record<string, VMPricingRow>>({});
  const [diskPricing, setDiskPricing] = useState<Record<string, DiskPricingRow>>({});
  const [addonPricing, setAddonPricing] = useState<Record<string, AddonPricingRow>>({});
  const [databasePricing, setDatabasePricing] = useState<Record<string, DatabasePricingRow>>({});
  const [databaseVMPricing, setDatabaseVMPricing] = useState<Record<string, DatabaseEditionPricingRow>>({});
  const [addonOptionPricing, setAddonOptionPricing] = useState<Record<string, AddonOptionPricingRow>>({});
  const [defaultAddons, setDefaultAddons] = useState<NormalizedDefaultAddon[]>([]);
  const [powerVMPricing, setPowerVMPricing] = useState<Record<string, PowerVMPricingRow>>({});
  const [powerAddonPricing, setPowerAddonPricing] = useState<Record<string, PowerAddonPricingRow>>({});
  const [powerAddonOptionPricing, setPowerAddonOptionPricing] = useState<Record<string, PowerAddonOptionPricingRow>>({});
  const [powerDefaultAddons, setPowerDefaultAddons] = useState<NormalizedPowerDefaultAddon[]>([]);
  const [powerDiskPricing, setPowerDiskPricing] = useState<Record<string, PowerDiskPricingRow>>({});

  /* -------- Backup & Storage pricing -------- */
  const [backupPricing, setBackupPricing] = useState<Record<string, BackupPricingRow>>({});
  const [databaseBackupPricing, setDatabaseBackupPricing] = useState<Record<string, DatabaseBackupPricingRow>>({});
  const [databaseExtraStoragePricing, setDatabaseExtraStoragePricing] = useState<Record<string, DatabaseExtraStoragePricingRow>>({});
  const [drTestingPricing, setDRTestingPricing] = useState<Record<string, DRTestingPricingRow>>({});

  /* -------- Quote state -------- */
  const [selectedProvider, setSelectedProvider] = useState<number | string>('');
  const [selectedRegion, setSelectedRegion] = useState<number | string>('');

  /* -------- UI state -------- */
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pricingReady, setPricingReady] = useState(false);
  const [isLoadingQuote, setIsLoadingQuote] = useState(false);

  const regionsRef = useRef<Region[]>([]);
  const selectedRegionRef = useRef<number | string>('');
  const desiredRegionIdRef = useRef<number | null>(null);
  const hasLoadedOnce = useRef(false);
  const pendingQuoteLoad = useRef(!!quoteIdFromRouteOrQuery);

  /* ---------------- Availability-filtered lists ---------------- */
  const filteredVMTypes = useMemo(() => {
    if (!selectedProvider || !selectedRegion || !pricingReady || isLoadingQuote) return vmTypes;
    return vmTypes.filter((v) =>
      osTypes.some((o) => {
        const p = vmPricing[`${v.VMTypeId}-${o.OSCategory}-${selectedRegion}`];
        return p && p.IsAvailable;
      })
    );
  }, [vmTypes, osTypes, selectedProvider, selectedRegion, vmPricing, pricingReady, isLoadingQuote]);

  const filteredOSTypes = useMemo(() => {
    if (!selectedProvider || !selectedRegion || !pricingReady || isLoadingQuote) return osTypes;
    return osTypes.filter((o) =>
      vmTypes.some((v) => {
        const p = vmPricing[`${v.VMTypeId}-${o.OSCategory}-${selectedRegion}`];
        return p && p.IsAvailable;
      })
    );
  }, [osTypes, vmTypes, selectedProvider, selectedRegion, vmPricing, pricingReady, isLoadingQuote]);

  const filteredDisks = useMemo(() => {
    if (!selectedProvider || !selectedRegion || !pricingReady || isLoadingQuote) return disks;
    return disks.filter((d) => {
      const p = diskPricing[`${d.DiskId}-${selectedRegion}`];
      return p && p.IsAvailable;
    });
  }, [disks, selectedProvider, selectedRegion, diskPricing, pricingReady, isLoadingQuote]);

  const filteredPowerDisks = useMemo(() => {
    if (!selectedProvider || !selectedRegion || !pricingReady || isLoadingQuote) return disks;
    return disks.filter((d) => {
      const p = powerDiskPricing[`${d.DiskId}-${selectedRegion}`];
      return p && p.IsAvailable;
    });
  }, [disks, selectedProvider, selectedRegion, powerDiskPricing, pricingReady, isLoadingQuote]);

  const filteredDatabaseTypes = useMemo(() => {
    if (!selectedProvider || !selectedRegion || !pricingReady || isLoadingQuote) return databaseTypes;
    return databaseTypes.filter((dt) => {
      if (dt.DeploymentType !== 'vm_based') {
        const typeConfigs = databaseConfigs.filter((dc) => dc.DatabaseTypeId === dt.DatabaseTypeId);
        return typeConfigs.some((config) => {
          const p = databasePricing[`${config.DatabaseConfigId}-${selectedRegion}`];
          return p && p.IsAvailable;
        });
      }
      const typeEditions = databaseEditions.filter((de) => de.DatabaseTypeId === dt.DatabaseTypeId);
      if (typeEditions.length === 0) return false;
      return typeEditions.some((edition) =>
        Object.keys(databaseVMPricing).some((key) => {
          const [, editionId, regionId] = key.split('-');
          return (
            parseInt(editionId!, 10) === edition.DatabaseEditionId &&
            parseInt(regionId!, 10) === parseInt(String(selectedRegion), 10) &&
            databaseVMPricing[key]!.IsAvailable
          );
        })
      );
    });
  }, [
    databaseTypes, databaseConfigs, selectedProvider, selectedRegion,
    databasePricing, databaseEditions, databaseVMPricing, pricingReady, isLoadingQuote,
  ]);

  const filteredDatabaseConfigs = useMemo(() => {
    if (!selectedProvider || !selectedRegion || !pricingReady || isLoadingQuote) return databaseConfigs;
    return databaseConfigs.filter((dc) => {
      const p = databasePricing[`${dc.DatabaseConfigId}-${selectedRegion}`];
      return p && p.IsAvailable;
    });
  }, [databaseConfigs, selectedProvider, selectedRegion, databasePricing, pricingReady, isLoadingQuote]);

  const filteredAddons = useMemo(() => {
    if (!selectedProvider || !selectedRegion || !pricingReady || isLoadingQuote) return addons;
    return addons.filter((a) => {
      const base = addonPricing[`${a.AddonId}-${selectedRegion}`];
      const hasBase = !!(base && base.IsAvailable);
      const options = addonOptions[a.AddonId] ?? [];
      const hasOption = options.some((opt) => {
        const p = addonOptionPricing[`${opt.AddonOptionId}-${selectedRegion}`];
        return p && p.IsAvailable;
      });
      return hasBase || hasOption;
    });
  }, [addons, selectedProvider, selectedRegion, addonPricing, addonOptions, addonOptionPricing, pricingReady, isLoadingQuote]);

  /* ---------------- Data fetching ---------------- */
  useEffect(() => { fetchInitialData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { regionsRef.current = regions; }, [regions]);
  useEffect(() => { selectedRegionRef.current = selectedRegion; }, [selectedRegion]);

  const fetchRegions = useCallback(async () => {
    if (!selectedProvider) return;
    try {
      const response = await regionsAPI.getAll(selectedProvider as number);
      const activeRegions: Region[] = response.data.filter((r: Region) => r.IsActive);
      const currentRegionName = regionsRef.current.find((r) => r.RegionId === selectedRegionRef.current)?.RegionName;
      setRegions(activeRegions);
      regionsRef.current = activeRegions;

      if (pendingQuoteLoad.current) {
        if (desiredRegionIdRef.current) {
          const matchById = activeRegions.find((r) => r.RegionId === desiredRegionIdRef.current);
          if (matchById) {
            setSelectedRegion(matchById.RegionId);
            selectedRegionRef.current = matchById.RegionId;
            desiredRegionIdRef.current = null;
            pendingQuoteLoad.current = false;
          }
        }
        return;
      }

      if (desiredRegionIdRef.current) {
        const matchById = activeRegions.find((r) => r.RegionId === desiredRegionIdRef.current);
        if (matchById) {
          setSelectedRegion(matchById.RegionId);
          selectedRegionRef.current = matchById.RegionId;
          desiredRegionIdRef.current = null;
          return;
        }
      }

      if (currentRegionName) {
        const match = activeRegions.find((r) => r.RegionName === currentRegionName);
        if (match) { setSelectedRegion(match.RegionId); selectedRegionRef.current = match.RegionId; return; }
      }

      if (activeRegions.length > 0) {
        setSelectedRegion(activeRegions[0]!.RegionId);
        selectedRegionRef.current = activeRegions[0]!.RegionId;
      } else {
        setSelectedRegion('');
        selectedRegionRef.current = '';
      }
    } catch {
      setError('Failed to load regions');
    }
  }, [selectedProvider]);

  useEffect(() => { if (selectedProvider) fetchRegions(); }, [selectedProvider, fetchRegions]);

  const fetchInitialData = async () => {
    try {
      const [providersRes, environmentsRes, vmTypesRes, osTypesRes, disksRes, dbTypesRes, dbConfigsRes, addonsRes, dbEditionsRes, powerTypesRes, powerAddonsRes] = await Promise.all([
        providersAPI.getAll(),
        environmentsAPI.getAll(),
        vmTypesAPI.getAll(),
        osTypesAPI.getAll(),
        disksAPI.getAll(),
        databaseTypesAPI.getAll(),
        databaseConfigsAPI.getAll(),
        addonsAPI.getAll(),
        databaseEditionsAPI.getAll().catch(() => ({ data: [] })),
        powerTypesAPI.getAll().catch(() => ({ data: [] })),
        powerAddonsAPI.getAll().catch(() => ({ data: [] })),
      ]);

      const activeProviders: Provider[] = providersRes.data.filter((p: Provider) => p.IsActive);
      setProviders(activeProviders);
      setEnvironments(environmentsRes.data);
      setVMTypes(vmTypesRes.data.filter((v: VMType) => v.IsActive));
      setOSTypes(osTypesRes.data.filter((o: OSType) => o.IsActive));
      setDisks(disksRes.data.filter((d: Disk) => d.IsActive));
      setDatabaseTypes(dbTypesRes.data.filter((d: DatabaseType) => d.IsActive));
      setDatabaseConfigs(dbConfigsRes.data.filter((d: DatabaseConfig) => d.IsActive));
      setDatabaseEditions(dbEditionsRes.data.filter((d: DatabaseEdition) => d.IsActive));
      setPowerTypes(powerTypesRes.data.filter((pt: PowerType) => pt.IsActive));
      const activePowerAddons: PowerAddon[] = powerAddonsRes.data.filter((pa: PowerAddon) => pa.IsActive);
      setPowerAddons(activePowerAddons);

      try {
        const allPowerOptsRes = await powerAddonOptionsAPI.getAll();
        const powerOptsMap: Record<number, PowerAddonOption[]> = {};
        activePowerAddons.forEach((pa) => { powerOptsMap[pa.PowerAddonId] = []; });
        allPowerOptsRes.data.filter((o: PowerAddonOption) => o.IsActive).forEach((opt: PowerAddonOption) => {
          if (!powerOptsMap[opt.PowerAddonId]) powerOptsMap[opt.PowerAddonId] = [];
          powerOptsMap[opt.PowerAddonId]!.push(opt);
        });
        setPowerAddonOptions(powerOptsMap);
      } catch {
        setPowerAddonOptions({});
      }

      const activeAddons: Addon[] = addonsRes.data.filter((a: Addon) => a.IsActive);
      setAddons(activeAddons);

      try {
        const allOptsRes = await addonOptionsAPI.getAll();
        const optionsMap: Record<number, AddonOption[]> = {};
        activeAddons.forEach((a) => { optionsMap[a.AddonId] = []; });
        allOptsRes.data.filter((o: AddonOption) => o.IsActive).forEach((opt: AddonOption) => {
          if (!optionsMap[opt.AddonId]) optionsMap[opt.AddonId] = [];
          optionsMap[opt.AddonId]!.push(opt);
        });
        setAddonOptions(optionsMap);
      } catch {
        setAddonOptions({});
      }

      if (activeProviders.length > 0 && !quoteIdFromRouteOrQuery) setSelectedProvider(activeProviders[0]!.ProviderId);

      setLoading(false);
      hasLoadedOnce.current = true;
    } catch {
      setError('Failed to load initial data');
      setLoading(false);
    }
  };

  const fetchPricing = useCallback(async () => {
    if (!selectedProvider || !selectedRegion) return;
    try {
      setPricingReady(false);
      const [vmRes, diskRes, dbRes, addonRes, addonOptRes, defAddonsRes] = await Promise.all([
        pricingAPI.getVMPricing(selectedProvider as number),
        pricingAPI.getDiskPricing(selectedProvider as number),
        pricingAPI.getDatabasePricing(selectedProvider as number),
        pricingAPI.getAddonPricing(selectedProvider as number),
        pricingAPI.getAddonOptionPricing(selectedProvider as number),
        defaultAddonsAPI.getAll(selectedProvider as number, selectedRegion as number),
      ]);

      const vmMap: Record<string, VMPricingRow> = {};
      vmRes.data.forEach((p: VMPricingRow) => { vmMap[`${p.VMTypeId}-${p.OSCategory}-${p.RegionId}`] = p; });
      setVMPricing(vmMap);

      const diskMap: Record<string, DiskPricingRow> = {};
      diskRes.data.forEach((p: DiskPricingRow) => { diskMap[`${p.DiskId}-${p.RegionId}`] = p; });
      setDiskPricing(diskMap);

      const dbMap: Record<string, DatabasePricingRow> = {};
      dbRes.data.forEach((p: DatabasePricingRow) => { dbMap[`${p.DatabaseConfigId}-${p.RegionId}`] = p; });
      setDatabasePricing(dbMap);

      try {
        const dbVMRes = await pricingAPI.getDatabaseVMPricing(selectedProvider as number);
        const dbVMMap: Record<string, DatabaseEditionPricingRow> = {};
        dbVMRes.data.forEach((p: DatabaseEditionPricingRow) => { dbVMMap[`${p.VMTypeId}-${p.DatabaseEditionId}-${p.RegionId}`] = p; });
        setDatabaseVMPricing(dbVMMap);
      } catch {
        setDatabaseVMPricing({});
      }

      const addonMap: Record<string, AddonPricingRow> = {};
      addonRes.data.forEach((p: AddonPricingRow) => { addonMap[`${p.AddonId}-${p.RegionId}`] = p; });
      setAddonPricing(addonMap);

      const addonOptMap: Record<string, AddonOptionPricingRow> = {};
      addonOptRes.data.forEach((p: AddonOptionPricingRow) => { addonOptMap[`${p.AddonOptionId}-${p.RegionId}`] = p; });
      setAddonOptionPricing(addonOptMap);

      setDefaultAddons(
        ((defAddonsRes.data ?? []) as Record<string, unknown>[])
          .filter((da) => da.IsEnabled)
          .map((da) => ({
            DefaultAddonId: da.DefaultAddonId as number,
            AddonId: Number(da.AddonId),
            ProviderId: da.ProviderId as number,
            RegionId: da.RegionId as number,
            EnvironmentId: Number(da.EnvironmentId),
            EnvironmentCode: (da.EnvironmentCode as string) ?? '',
            ApplyToVMs: Boolean(da.ApplyToVMs),
            ApplyToDatabaseVMs: Boolean(da.ApplyToDatabaseVMs),
            IsEnabled: da.IsEnabled as number,
          }))
      );

      // Fetch Backup & Storage pricing
      try {
        const [bkpRes, dbBkpRes, dbExtraRes] = await Promise.all([
          backupPricingAPI.getVMBackupPricing(selectedProvider as number),
          backupPricingAPI.getDatabaseBackupPricing(selectedProvider as number),
          backupPricingAPI.getDatabaseExtraStoragePricing(selectedProvider as number),
        ]);

        const bkpMap: Record<string, BackupPricingRow> = {};
        bkpRes.data.forEach((p: BackupPricingRow) => { bkpMap[`${p.RegionId}`] = p; });
        setBackupPricing(bkpMap);

        const dbBkpMap: Record<string, DatabaseBackupPricingRow> = {};
        dbBkpRes.data.forEach((p: DatabaseBackupPricingRow) => { dbBkpMap[`${p.RegionId}`] = p; });
        setDatabaseBackupPricing(dbBkpMap);

        const dbExtraMap: Record<string, DatabaseExtraStoragePricingRow> = {};
        dbExtraRes.data.forEach((p: DatabaseExtraStoragePricingRow) => { dbExtraMap[`${p.RegionId}`] = p; });
        setDatabaseExtraStoragePricing(dbExtraMap);

        // Fetch DR testing pricing
        const drRes = await drTestingPricingAPI.getAll(selectedProvider as number);
        const drMap: Record<string, DRTestingPricingRow> = {};
        drRes.data.forEach((p: DRTestingPricingRow) => { drMap[`${p.RegionId}`] = p; });
        setDRTestingPricing(drMap);
      } catch {
        setBackupPricing({});
        setDatabaseBackupPricing({});
        setDatabaseExtraStoragePricing({});
        setDRTestingPricing({});
      }

      // Fetch Power VM pricing data (only for IBM providers)
      try {
        const [pvmRes, paRes, paOptRes, pDefAddonsRes, pdiskRes] = await Promise.all([
          pricingAPI.getPowerVMPricing(selectedProvider as number),
          pricingAPI.getPowerAddonPricing(selectedProvider as number),
          pricingAPI.getPowerAddonOptionPricing(selectedProvider as number),
          powerDefaultAddonsAPI.getAll(selectedProvider as number, selectedRegion as number),
          pricingAPI.getPowerDiskPricing(selectedProvider as number),
        ]);

        const pvmMap: Record<string, PowerVMPricingRow> = {};
        pvmRes.data.forEach((p: PowerVMPricingRow) => { pvmMap[`${p.PowerTypeId}-${p.RegionId}`] = p; });
        setPowerVMPricing(pvmMap);

        const paMap: Record<string, PowerAddonPricingRow> = {};
        paRes.data.forEach((p: PowerAddonPricingRow) => { paMap[`${p.PowerAddonId}-${p.RegionId}`] = p; });
        setPowerAddonPricing(paMap);

        const paOptMap: Record<string, PowerAddonOptionPricingRow> = {};
        paOptRes.data.forEach((p: PowerAddonOptionPricingRow) => { paOptMap[`${p.PowerAddonOptionId}-${p.RegionId}`] = p; });
        setPowerAddonOptionPricing(paOptMap);

        const pdiskMap: Record<string, PowerDiskPricingRow> = {};
        pdiskRes.data.forEach((p: PowerDiskPricingRow) => { pdiskMap[`${p.DiskId}-${p.RegionId}`] = p; });
        setPowerDiskPricing(pdiskMap);

        setPowerDefaultAddons(
          ((pDefAddonsRes.data ?? []) as Record<string, unknown>[])
            .filter((da) => da.IsEnabled)
            .map((da) => ({
              PowerDefaultAddonId: da.PowerDefaultAddonId as number,
              PowerAddonId: Number(da.PowerAddonId),
              ProviderId: da.ProviderId as number,
              RegionId: da.RegionId as number,
              EnvironmentId: Number(da.EnvironmentId),
              EnvironmentCode: (da.EnvironmentCode as string) ?? '',
              IsEnabled: da.IsEnabled as number,
            }))
        );
      } catch {
        setPowerVMPricing({});
        setPowerAddonPricing({});
        setPowerAddonOptionPricing({});
        setPowerDefaultAddons([]);
        setPowerDiskPricing({});
      }

      setPricingReady(true);
    } catch {
      setError('Failed to load pricing data');
      setPricingReady(true);
    }
  }, [selectedProvider, selectedRegion]);

  useEffect(() => { if (selectedProvider && selectedRegion) fetchPricing(); }, [selectedProvider, selectedRegion, fetchPricing]);

  return {
    // Catalog data
    providers, regions, environments, vmTypes, osTypes, disks,
    databaseTypes, databaseConfigs, databaseEditions, addons, addonOptions,
    powerTypes, powerAddons, powerAddonOptions,
    // Pricing
    vmPricing, diskPricing, addonPricing, databasePricing, databaseVMPricing, addonOptionPricing,
    defaultAddons,
    powerVMPricing, powerAddonPricing, powerAddonOptionPricing, powerDefaultAddons, powerDiskPricing,
    // Backup & Storage pricing
    backupPricing, databaseBackupPricing, databaseExtraStoragePricing,
    // DR testing pricing
    drTestingPricing,
    // Filtered lists
    filteredVMTypes, filteredOSTypes, filteredDisks, filteredPowerDisks,
    filteredDatabaseTypes, filteredDatabaseConfigs, filteredAddons,
    // Quote state
    selectedProvider, setSelectedProvider, selectedRegion, setSelectedRegion,
    // UI state
    loading, error, setError, pricingReady, isLoadingQuote, setIsLoadingQuote, setPricingReady,
    // Refs
    hasLoadedOnce, desiredRegionIdRef, pendingQuoteLoad,
    // Route info
    quoteIdFromRouteOrQuery,
  };
};
